import { useRouter } from "next/router";
import { hasDeclinedConsent } from "utils/consent";
import { GooseArticleQuery, ArticleQuery } from "types/api";
import getConfig from "next/config";
import { useEffect } from "react";
import { AdTemplatesEnum, registerAdsPageView } from "utils/ads";
import { loadVWO } from "utils/vwo";
import { ArticleLayout } from "types/api";

type InitializationEvent = {
  "gtm.start": number;
  event: "gtm.js";
};

type LandingPageViewEvent = {
  event: "pageview";
  page: {
    type: "landing";
    title: string;
  };
};

type ArticlePageViewEvent = {
  event: "pageview";
  page: {
    type: "article";
    title: string;
    article: ArticleQuery["article"] | GooseArticleQuery["article"];
    template: string;
    layout: string;
  };
};

type ErrorPageViewEvent = {
  event: "pageview";
  page: {
    type: "error";
    title: string;
    statusCode: number;
  };
};

type HomepagePageViewEvent = {
  event: "pageview";
  page: {
    type: "homepage";
    template: AdTemplatesEnum.homepage;
  };
};

type NewsletterSignup = {
  event: "Newsletter Signup";
  user: {
    email: string;
    newsletter: {
      id: string;
      name?: string;
    };
  };
};

type NudgeScroll = {
  event: "Nudge Scroll";
  category: string;
  action: string;
  label: string;
};

type RecircLoad = {
  event: "Recirc Load";
  recircServiceUsed: string;
};

type RelatedContentLocation = {
  event: "Related Content Location";
  relatedContentLocation: string;
};

type OptimizeTest = {
  event: "Optimize Test";
  abTest: {
    experiment_id: string;
    experiment_variant: number;
    abt_experiment: string | null;
    abt_value: string;
  };
};

type DataLayerValues =
  | InitializationEvent
  | LandingPageViewEvent
  | ArticlePageViewEvent
  | ErrorPageViewEvent
  | HomepagePageViewEvent
  | NewsletterSignup
  | NudgeScroll
  | OptimizeTest
  | RecircLoad
  | RelatedContentLocation;

type DataLayer<T = unknown> = T[];

declare global {
  interface Window {
    dataLayer: DataLayer;
  }
}

/**
 * Returns an dataLayer compatible array that variables and events can be
 * pushed into.
 */
export const getDataLayer = <T = DataLayerValues>() => {
  if (typeof window === "undefined") return [];

  window.dataLayer = window.dataLayer || [];
  return window.dataLayer as DataLayer<T>;
};

/**
 * Checks for consent status and loads a different GTM depending on if a user
 * has consented or not.
 */
export const loadDataLayer = async () => {
  if (typeof document === "undefined") return;

  const { publicRuntimeConfig } = getConfig();

  const declinedConsent = await hasDeclinedConsent();

  const containerId = declinedConsent
    ? publicRuntimeConfig.GTM_CONTAINER_ID_NONCONSENTED
    : publicRuntimeConfig.GTM_CONTAINER_ID;

  if (!declinedConsent) {
    loadVWO();
  }

  const dataLayer = getDataLayer();
  dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.body.appendChild(script);
};

/**
 * Track a pageview of a landing page.
 */
export const trackLandingPageView = ({
  displayName,
  template,
}: {
  displayName: string;
  template: AdTemplatesEnum;
}) => {
  const dataLayer = getDataLayer();

  dataLayer.push({
    event: "pageview",
    page: {
      type: "landing",
      title: displayName,
      template: template,
    },
  });
};

/**
 * Track a pageview of an article page.
 */
const trackArticlePageView = (
  {
    article,
  }: {
    article: GooseArticleQuery["article"] | ArticleQuery["article"];
  },
  template: AdTemplatesEnum
) => {
  const dataLayer = getDataLayer();

  dataLayer.push({
    event: "pageview",
    page: {
      type: "article",
      title: article.title,
      article: article,
      template: template,
      layout: "standard",
    },
  });
};

/**
 * Track a pageview of the homepage.
 */
export const trackHomepagePageView = () => {
  const dataLayer = getDataLayer();

  dataLayer.push({
    event: "pageview",
    page: {
      type: "homepage",
      template: AdTemplatesEnum.homepage,
    },
  });
};

function articleIsSwan(
  article: GooseArticleQuery["article"] | ArticleQuery["article"]
): article is ArticleQuery["article"] {
  return "content" in article;
}

/**
 * A React hook that tracks article page views and registers them in ads.js
 */
export const useTrackPageView = (
  { article }: GooseArticleQuery | ArticleQuery,
  ads = true,
  analytics = true
) => {
  const router = useRouter();

  const isAppWebview = router && router.pathname === "/app/webview/[id]";

  const categorySlugs = article.categories.map(({ slug }) => slug);
  const channelSlugs = article.channels.map(({ slug }) => slug);
  const isMagArticle = article.__typename === "MagazineArticle" ? true : false;
  const issue =
    article.__typename === "MagazineArticle" ? article.issue : undefined;
  let ad_unit_template: AdTemplatesEnum;
  switch (article.layout) {
    case ArticleLayout.Feature:
      ad_unit_template = AdTemplatesEnum.article_feature;
      break;
    case ArticleLayout.Twocol:
      ad_unit_template = AdTemplatesEnum.article_twocol;
      break;
    case ArticleLayout.Standard:
      ad_unit_template = AdTemplatesEnum.article_standard;
      break;
  }

  useEffect(() => {
    // Track the article in GTM
    if (analytics) {
      trackArticlePageView({ article }, ad_unit_template);
    }

    if (ads) {
      // Register the pageview in ads.js
      registerAdsPageView({
        platform: isAppWebview ? "android" : "web",
        lazy_load: 2,
        channel: article.primaryChannel?.slug || "", // primary Channel
        template: ad_unit_template,
        datePublished: article.datePublished || "",
        globalTargeting: {
          title: article.slug || "", // article slug
          grapeshot_segments: article.grapeshot?.segments || [], // brand safety list
          cat: categorySlugs, // slugs of all the categories the article is tagged with
          rubric: article.primaryChannel?.displayName || "", // primary channel name
          id: article.id.split(":")[1], // story ID
          xpost: channelSlugs, // slugs of all channels the article is tagged with
          type: isMagArticle ? "mag-reg" : "non-mag-reg", // is it a magazine article
          iss: issue?.slug || undefined, // issue slug if it's a magazine, or undefined
          issue_name: issue?.issueName || undefined, // issue name if it's a magazine, or undefined
          report: article.editorialProject?.slug || "", // Special report slug
          nopromo:
            article.advertisingInfo?.disableHouseAd.toString() || "false",
          experiment: articleIsSwan(article) ? "swan.release" : "swan.control",
        },
      });
    }
  }, [article.id]); //eslint-disable-line react-hooks/exhaustive-deps
};

export const trackErrorPageView = ({
  title,
  statusCode,
}: {
  statusCode: number;
  title: string;
}) => {
  const dataLayer = getDataLayer();

  dataLayer.push({
    event: "pageview",
    page: {
      type: "error",
      title,
      statusCode,
    },
  });
};
